
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, gql, useMutation} from '@apollo/client';
import throttle from 'lodash.throttle';
import {
  Input,
  Divider,
  Accordion,
  Dropdown,
  Button,
  Dimmer,
  Loader,
  Icon,
  Container,
  Header,
  Segment,
  Label,
  Message,
} from 'semantic-ui-react'
import DatePicker from "react-datepicker";

import { PATHS } from '../../../../paths';
import { formatDateTime, formatDateTimeToDay } from "../../../../dates";

import styles from './AdminClassView.module.css';

import { AttendanceGraph } from "../../../../components/Protected/AttendanceGraph";
import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';
import { Session } from '../../../../components/Session';
import { useIsAdmin } from "../../../../hooks/isAdmin";
import { useBlocks } from "../../../../hooks/useBlocks";

import { CourseAdmin } from '../CourseAdmin';
import { CourseTags } from '../CourseTags';
import { CourseNameField } from '../../../../components/CourseNameField';
import { validateCourseName } from '../../../../utils/courseName';

const GET_COURSE = gql`
  query Courses($courseId: Int!) {
    course(courseId: $courseId) {
      id
      name
      canEdit
      SKU
      archivedAt
      absenceLimit
      managerUserId
      manager {
          id
          name
      }
      attendancePercentage
      teachers {
          id
          name
      }
      courseParticipantCount
      tags {
        id
        name
      }
      futureParticipants {
          id
          name
      }
      # Everyone who has ever been on a session of this class, not just those still on a future
      # one — a student who stopped attending is exactly the one who needs moving.
      participants {
          id
          name
          identifier
      }
      sessions {
        id
        course {
            id
            name
            SKU
        }
        durationMinutes
        attendance {
            details
            status
            id
            isTeacher
            user {
                name
                id
                identifier
                photoUrl
            }
        }
        startTime
        attendanceCount
        reasonForCancellation
        attendancePercentage
      }
    }
  }`;

const GET_TEACHER_CANDIDATES = gql`
  query TeacherCandidates {
    teacherCandidates { id name }
  }
`;

const GET_MANAGER_CANDIDATES = gql`
  query CourseManagerCandidates {
    courseManagerCandidates { id name }
    courseManagerCandidatesWithoutEmail
  }
`;

const SET_COURSE_MANAGER = gql`
  mutation SetCourseManager($courseId: Int!, $managerUserId: Int) {
    setCourseManager(courseId: $courseId, managerUserId: $managerUserId) {
      status
    }
  }
`;

const ADD_TEACHER = gql`
  mutation AddTeacherToCourse($courseId: Int!, $userId: Int!) {
    addTeacherToCourse(courseId: $courseId, userId: $userId) { status }
  }
`;

const REMOVE_TEACHER = gql`
  mutation RemoveTeacherFromCourse($courseId: Int!, $userId: Int!) {
    removeTeacherFromCourse(courseId: $courseId, userId: $userId) { status }
  }
`;

const ADD_PERSON_TO_SESSION = gql`
mutation AddPersonToSession($sessionId: Int!, $identifier: String, $status: String) {
  addPersonToSession(sessionId: $sessionId, identifier: $identifier, status: $status) {
    id
  }
}`;

const DELETE_SESSION = gql`
mutation Mutation($sessionId: Int!, $reason: String) {
  cancelSession(sessionId: $sessionId, reason: $reason) {
    id
  }
}`;

const ADD_SESSION_TO_COURSE = gql`
mutation AddSession($courseId: Int!, $date: String) {
  addSession(courseId: $courseId, date: $date) {
    id
  }
}
`;

const UPDATE_SESSION_TIME = gql`
mutation UpdateSessionTime($sessionId: Int!, $magnitude: Int!, $forwardDirection: Boolean!, $unit: String!) {
  updateSessionTime(sessionId: $sessionId, magnitude: $magnitude, forwardDirection: $forwardDirection, unit: $unit) {
    id
  }
}`;

const DUPLICATE_COURSE = gql`
  mutation Mutation($courseToCopyId: Int!, $newCourseName: String!, $daysToCopyItForward: Int!, $newBlockTagId: Int!, $oldBlockTagId: Int!) {
    duplicateCourse(courseToCopyId: $courseToCopyId, newCourseName: $newCourseName, daysToCopyItForward: $daysToCopyItForward, newBlockTagId: $newBlockTagId, oldBlockTagId: $oldBlockTagId) {
      status
    }
  }`;

const DELETE_COURSE = gql`
  mutation Mutation($courseId: Int!) {
    deleteCourse(courseId: $courseId) {
      status
    }
  }`;

const ARCHIVE_COURSE = gql`
  mutation ArchiveCourse($courseId: Int!) {
    archiveCourse(courseId: $courseId) {
      id
      archivedAt
    }
  }`;

const RENAME_COURSE = gql`
  mutation RenameCourse($courseId: Int!, $newCourseName: String!) {
    renameCourse(courseId: $courseId, newCourseName: $newCourseName) {
      status
    }
  }`

const SET_COURSE_ABSENCE_LIMIT = gql`
  mutation SetCourseAbsenceLimit($courseId: Int!, $absenceLimit: Int) {
    setCourseAbsenceLimit(courseId: $courseId, absenceLimit: $absenceLimit) {
      status
    }
  }`

const REMOVE_FUTURE_TEACHER_ATTENDANCE = gql`
  mutation RemoveFutureTeacherAttendance($courseId: Int!) {
    removeFutureTeacherAttendance(courseId: $courseId) {
      status
    }
  }`

const ADD_FUTURE_TEACHER_ATTENDANCE = gql`
  mutation AddFutureTeacherAttendance($courseId: Int!) {
    addFutureTeacherAttendance(courseId: $courseId) {
      status
    }
  }`

const GET_BLOCK_TAGS = gql`
  query BlockTags {
    blockTags {
      id
      name
    }
  }`

// The classes a student can be moved to: the other classes in this class's block.
const GET_CLASSES_IN_BLOCK = gql`
  query ClassesInBlock($filters: String) {
    classes(filters: $filters) {
      id
      name
      SKU
    }
  }`

const MOVE_STUDENT_TO_CLASS = gql`
  mutation MoveStudentToClass($studentUserId: Int!, $fromCourseId: Int!, $toCourseId: Int!) {
    moveStudentToClass(studentUserId: $studentUserId, fromCourseId: $fromCourseId, toCourseId: $toCourseId) {
      status
      sessionsCreated
      rowsMoved
      futureSessionsJoined
      rowsRemoved
    }
  }`


const CANCELLATION_REASON = [
  {
    key: 'Teacher-sick',
    text: 'Teacher sick',
    value:'Teacher sick',
  },
  {
    key: 'Teacher-holiday',
    text: 'Teacher holiday',
    value:'Teacher holiday',
  },
  {
    key: 'Company-cancelled',
    text: 'Company cancelled',
    value:'Company cancelled',
  },
  {
    key: 'Other',
    text: 'Other',
    value:'Other',
  }
]

const AdminClassView = () => {
  let navigate = useNavigate();
  let { id } = useParams();
  const isAdmin = useIsAdmin();
  const { currentBlockTagId, nextBlockTagId } = useBlocks();

  const [disableDuplicate, setDisableDuplicate] = useState(false);
  const [newCourseNameDuplicate, setNewCourseNameDuplicate] = useState('');
  const [newCourseName, setNewCourseName] = useState('');
  const [daysToCopyItForward, setDaysToCopyItForward] = useState(0);
  const { loading, error, data, refetch } = useQuery(GET_COURSE, { variables: { courseId: Number(id) } });

  const { data: teacherCandidateData } = useQuery(GET_TEACHER_CANDIDATES);

  // Only an admin can set a manager, and the server answers [] for anybody else — asking for
  // the list on every teacher's class page would be a request that can only come back empty.
  const { data: managerCandidateData } = useQuery(GET_MANAGER_CANDIDATES, { skip: !isAdmin });

  const [addTeacherToCourse] = useMutation(ADD_TEACHER);

  const [removeTeacherFromCourse] = useMutation(REMOVE_TEACHER);

  const [teacherBusy, setTeacherBusy] = useState(false);

  const [teacherError, setTeacherError] = useState<string | null>(null);


  // Both mutations are roster operations: adding restores an archived assignment rather

  // than making a second one, and removing archives rather than deletes, so the class

  // keeps showing who taught it. Refetch rather than patch the cache — removing a teacher

  // also clears their future attendance rows, which this query reads.

  const changeTeacher = async (userId: number, add: boolean) => {

    setTeacherBusy(true);

    setTeacherError(null);

    try {

      const mutate = add ? addTeacherToCourse : removeTeacherFromCourse;

      await mutate({ variables: { courseId: Number(id), userId } });

      await refetch({ variables: { courseId: Number(id) } });

    } catch (err) {

      setTeacherError(err instanceof Error ? err.message : 'That did not work');

    } finally {

      setTeacherBusy(false);

    }

  };


  const [addPersonToSession] = useMutation(ADD_PERSON_TO_SESSION);
  const [deleteSession] = useMutation(DELETE_SESSION);

  const [newSessionDate, setNewSessionDate] = useState<Date | null>(new Date());
  const [addSessionToCourse] = useMutation(ADD_SESSION_TO_COURSE);
  const [updateSessionTime] = useMutation(UPDATE_SESSION_TIME);
  const [duplicateClass] = useMutation(DUPLICATE_COURSE);
  const [renameCourse] = useMutation(RENAME_COURSE);
  const [setAbsenceLimit] = useMutation(SET_COURSE_ABSENCE_LIMIT);
  const [setCourseManager, { loading: savingManager }] = useMutation(SET_COURSE_MANAGER);
  const [deleteCourse] = useMutation(DELETE_COURSE);
  const [archiveCourse] = useMutation(ARCHIVE_COURSE);
  const [removeFutureTeacherAttendance] = useMutation(REMOVE_FUTURE_TEACHER_ATTENDANCE);
  const [addFutureTeacherAttendance] = useMutation(ADD_FUTURE_TEACHER_ATTENDANCE);

  const [moveStudentToClass] = useMutation(MOVE_STUDENT_TO_CLASS);
  const [moveStudentId, setMoveStudentId] = useState<number | null>(null);
  const [moveTargetCourseId, setMoveTargetCourseId] = useState<number | null>(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);

  // Admin-only lists, like the manager candidates above: anybody else gets an empty answer.
  const { data: blockTagData } = useQuery(GET_BLOCK_TAGS, { skip: !isAdmin });

  // This class's block — the one tag it carries that belongs to the "block" tag group. A student
  // can only be moved within it, so this is also the list of possible destinations. Falls back to
  // the organisation's current block, which is the right answer for any class still running.
  const blockTagIds: number[] = (blockTagData?.blockTags ?? []).map((tag: any) => Number(tag.id));
  const courseBlockTagId: number | null =
    (data?.course?.tags ?? [])
      .map((tag: any) => Number(tag.id))
      .find((tagId: number) => blockTagIds.includes(tagId))
    ?? currentBlockTagId
    ?? null;

  const { data: blockClassData } = useQuery(GET_CLASSES_IN_BLOCK, {
    variables: { filters: `tagId:${courseBlockTagId}` },
    skip: !isAdmin || courseBlockTagId == null,
  });

  const [editMode, setEditMode] = useState(false);
  const [absenceLimitInput, setAbsenceLimitInput] = useState('');
  const [activeId, setActiveId] = useState();
  const [identifier, setIdentifier] = useState('');
  const [hasUsedAccordian, setHasUsedAccordian] = useState(false);

  const TenHoursAgo = Number(new Date()) - 10*60*60*1000;

  // A bad name is how a bad name propagates block to block, so duplication waits for a good one.
  const duplicateNameIsValid = validateCourseName(newCourseNameDuplicate).valid;

  // The last name we actually sent, so the throttled as-you-type save and an accepted
  // suggestion cannot both fire the same rename.
  const lastSavedName = useRef<string | null>(null);

  const saveCourseName = useCallback(async (newName: string) => {
      // The as-you-type path calls this mid-typing, so a half-typed name would reach the server
      // and be rejected. Only a valid name is worth saving.
      if (!validateCourseName(newName).valid || lastSavedName.current === newName) {
        return;
      }
      lastSavedName.current = newName;
      await renameCourse({ variables: { courseId: Number(id), newCourseName: newName } })
      refetch();
  }, [renameCourse, id, refetch]);

  const updateCourseName = useCallback(throttle((newName: string) => {
      saveCourseName(newName);
  }, 500, { leading: false, trailing: true }), [saveCourseName]);

  const saveAbsenceLimit = useCallback(async (raw: string) => {
      const trimmed = raw.trim();
      const value = trimmed === '' ? null : Number(trimmed);
      if (value != null && (isNaN(value) || value < 1)) {
        return;
      }
      await setAbsenceLimit({ variables: { courseId: Number(id), absenceLimit: value } });
      refetch();
  }, [setAbsenceLimit, id, refetch]);

  const updateAbsenceLimit = useCallback(throttle((raw: string) => {
      saveAbsenceLimit(raw);
  }, 500, { leading: false, trailing: true }), [saveAbsenceLimit]);

  // No throttle, unlike the two fields above: a dropdown produces one deliberate choice rather
  // than a value that changes on every keystroke.
  const saveManager = useCallback(async (managerUserId: number | null) => {
      await setCourseManager({ variables: { courseId: Number(id), managerUserId } });
      refetch();
  }, [setCourseManager, id, refetch]);

  // The whole move happens server-side in one transaction; this only confirms it and reloads the
  // class, which is now missing the student and their attendance.
  const moveStudent = async () => {
    if (moveStudentId == null || moveTargetCourseId == null) {
      return;
    }

    const student = (data?.course?.participants ?? []).find((p: any) => Number(p.id) === moveStudentId);
    const target = (blockClassData?.classes ?? []).find((c: any) => Number(c.id) === moveTargetCourseId);

    if (!window.confirm(
      `Move ${student?.name ?? 'this student'} to ${target?.name ?? 'the other class'}?\n\n` +
      "This will:\n" +
      "• Copy their attendance so far into the other class, at the times this class ran — same PRESENT/ABSENT marks, as history only, with no teacher attendance\n" +
      "• Add them to the other class's future sessions\n" +
      "• Remove them from this class completely, past sessions included\n" +
      "• Mark their record here \"Did not finish — Scheduling clash\", which is how block reports count a transfer rather than a drop-out\n\n" +
      "Press OK to move them, or Cancel to go back."
    )) {
      return;
    }

    setMoveBusy(true);
    setMoveError(null);

    try {
      await moveStudentToClass({
        variables: {
          studentUserId: moveStudentId,
          fromCourseId: Number(id),
          toCourseId: moveTargetCourseId,
        },
      });
      setMoveStudentId(null);
      setMoveTargetCourseId(null);
      await refetch({ variables: { courseId: Number(id) } });
    } catch (err) {
      setMoveError(err instanceof Error ? err.message : 'That did not work');
    } finally {
      setMoveBusy(false);
    }
  };

  useEffect(() => {
    if (!activeId && !loading && !error && data != null && data.course) {
      const nextSession = data.course.sessions.find(
        (s: {startTime:number, reasonForCancellation: string | null}) =>
              s.startTime > TenHoursAgo
          && (s.reasonForCancellation == null || s.reasonForCancellation === ""))

      if (nextSession != null && hasUsedAccordian === false) {
        setActiveId(nextSession.id)
      }
    }
  }, [loading, error, data, activeId, hasUsedAccordian])

  if (loading) {
    return (
      <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
          <Container>
            <Dimmer active inverted>
              <Loader inverted content='Loading' />
            </Dimmer>
          </Container>
        </Segment>
      </ResponsiveContainer>
    )
  }

  // check for no internet connection
  if (error && error.message === 'Failed to fetch') {
    return (
      <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
          <Container>
              <Header as='h2'>
                No internet connection
              </Header>
          </Container>
        </Segment>
      </ResponsiveContainer>
    )
  }

  if (!data || !data.course) {
    return (
      <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
          <Container>
              <Header as='h2'>
                Class not found
              </Header>
          </Container>
        </Segment>
      </ResponsiveContainer>
    )
  }

  if (error) {
    return <div />
  }

  // TODO: move Info section to its own component
  return (
    <ResponsiveContainer>
      <Segment key={id} style={{ padding: '2em 0em' }} vertical>
        <Container>
          <div className={styles.backRow}>
            <Icon name='arrow left' size='large' style={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
          </div>

          {/* Title + primary actions */}
          {data?.course?.archivedAt && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '0.6em 1em', marginBottom: '1em', display: 'flex', alignItems: 'center', gap: '0.5em' }}>
              <Icon name='archive' />
              <span><strong>This class is archived.</strong> Future sessions have been cancelled. Past attendance records are preserved.</span>
            </div>
          )}
          {!data?.course?.archivedAt && (() => {
            const sessions = data?.course?.sessions ?? [];
            const activeSessions = sessions.filter((s: any) => s.reasonForCancellation == null);
            const isHistoric = activeSessions.length === 0 || activeSessions.every((s: any) => Number(s.startTime) < Date.now());
            return isHistoric ? (
              <div style={{ background: '#e9ecef', border: '1px solid #ced4da', borderRadius: '4px', padding: '0.6em 1em', marginBottom: '1em', display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                <Icon name='history' />
                <span><strong>This class is historic.</strong> All sessions have passed.</span>
              </div>
            ) : null;
          })()}
          <div className={styles.titleRow}>
            {editMode && isAdmin ? (
              <CourseNameField
                value={newCourseName}
                sessions={data?.course?.sessions}
                onChange={(name: string) => {
                  setNewCourseName(name)
                  updateCourseName(name)
                }}
                // Accepting a suggestion, or a tidy on blur, saves straight away rather than
                // waiting on the throttle.
                onCommit={(name: string) => {
                  setNewCourseName(name)
                  saveCourseName(name)
                }}
              />
            ) : (
              <span className={styles.title}>{data?.course?.name}</span>
            )}

            {isAdmin && (
              <div className={styles.actions}>
                <Button
                  onClick={() => {
                    setEditMode(!editMode)
                    setNewCourseName(data && data.course && data.course.name)
                    setAbsenceLimitInput(data?.course?.absenceLimit != null ? String(data.course.absenceLimit) : '')
                  }}
                >
                  <Icon name='edit' /> {editMode ? "Done editing" : "Edit"}
                </Button>
                <Button onClick={() => navigate(`${PATHS.CLASSES}/${id}/records`)}>
                  <Icon name='file alternate' /> Records
                </Button>
              </div>
            )}
          </div>

          {/* Edit-mode admin panel: duplicate + delete */}
          {isAdmin && editMode && (
            <Segment secondary className={styles.adminPanel}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Absence limit (default: 2)</label>
                <Input
                  fluid
                  type='number'
                  min={1}
                  placeholder='2'
                  value={absenceLimitInput}
                  onChange={(e: any) => {
                    const raw = e.target.value;
                    setAbsenceLimitInput(raw);
                    updateAbsenceLimit(raw);
                  }}
                />
              </div>

              {(() => {
                const candidates = managerCandidateData?.courseManagerCandidates ?? [];
                const withoutEmail: number = managerCandidateData?.courseManagerCandidatesWithoutEmail ?? 0;
                const current = data?.course?.manager;

                // Nobody can be picked without an email, but somebody's address can be removed
                // after they were. Keeping them in the list is what stops the dropdown rendering
                // blank for a class that does have a manager — the warning below says why they
                // are no longer being written to.
                const options = [
                  ...candidates.map((c: any) => ({ key: c.id, value: c.id, text: c.name })),
                  ...(current && !candidates.some((c: any) => c.id === current.id)
                    ? [{ key: current.id, value: current.id, text: `${current.name} (no email address)` }]
                    : []),
                ];

                return (
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Class manager</label>
                    <Dropdown
                      fluid
                      selection
                      search
                      clearable
                      loading={savingManager}
                      disabled={savingManager}
                      placeholder='Nobody — this class is not reported to anyone'
                      noResultsMessage='Nobody with an email address'
                      value={data?.course?.managerUserId ?? ''}
                      options={options}
                      onChange={(_: any, { value }: any) =>
                        saveManager(value === '' || value == null ? null : Number(value))}
                    />
                    <p className={styles.muted}>
                      Emailed at 9am when a session of this class ran the day before and nobody
                      recorded the attendance. Does not grant any access to the class.
                    </p>
                    {/* The role does one thing — send an email — so a manager without an address
                        is a class that reports to nobody. The server refuses to set one, which
                        makes an empty list look like a bug unless it explains itself. */}
                    {withoutEmail > 0 && (
                      <Message warning size='tiny'>
                        <Icon name='mail' />
                        {candidates.length === 0
                          ? `Nobody can be chosen yet — all ${withoutEmail} staff here have no email address on file.`
                          : `${withoutEmail} other ${withoutEmail === 1 ? 'person is' : 'people are'} not listed: no email address on file.`}
                        {' '}A manager needs one to be told about missing attendance.
                      </Message>
                    )}
                  </div>
                );
              })()}

              <Divider />

              {nextBlockTagId != null ? (
                <>
                  <Header as='h4'>Duplicate class for next block</Header>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>New class name</label>
                    <CourseNameField
                      value={newCourseNameDuplicate}
                      onChange={setNewCourseNameDuplicate}
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Days to copy forward</label>
                    <Input
                      fluid
                      placeholder='Days to copy it forward'
                      value={daysToCopyItForward}
                      onChange={(e: any) => {
                        const value = Number(e.target.value)
                        if (value < 0 || isNaN(value)) {
                          return
                        }
                        setDaysToCopyItForward(Number(e.target.value))
                      }}
                    />
                  </div>
                  <p className={styles.muted}>
                    First class: {data && data.course && data.course.sessions.length > 0 && formatDateTime(Number(data.course.sessions[0].startTime) + daysToCopyItForward * 24 * 60 * 60 * 1000)}
                  </p>
                  <Button
                    primary
                    disabled={disableDuplicate || !duplicateNameIsValid || daysToCopyItForward === 0}
                    onClick={async () => {
                      await duplicateClass({ variables: {
                        courseToCopyId: Number(id),
                        newCourseName: newCourseNameDuplicate,
                        daysToCopyItForward,
                        newBlockTagId: nextBlockTagId,
                        oldBlockTagId: currentBlockTagId,
                      }});
                      setDisableDuplicate(true)
                    }}
                  >
                    Duplicate for next block
                  </Button>
                </>
              ) : (
                <p className={styles.muted}>The next block has not been created yet.</p>
              )}

              <Divider />

              <Button
                color='orange'
                basic
                icon='archive'
                content={data?.course?.archivedAt ? 'Already archived' : 'Archive class'}
                disabled={!!data?.course?.archivedAt}
                onClick={async () => {
                  if (window.confirm(
                    "Archive this class?\n\n" +
                    "This will:\n" +
                    "• Cancel all future sessions\n" +
                    "• Hide the class from teacher views\n\n" +
                    "Attendance records from sessions that have already run will be kept.\n\n" +
                    "Press OK to archive, or Cancel to go back."
                  )) {
                    await archiveCourse({ variables: { courseId: Number(id) } });
                    refetch({ variables: { courseId: Number(id) } });
                  }
                }}
              />

              <Divider />

              <Button
                color='teal'
                basic
                icon='user times'
                content='Remove future teacher attendance'
                onClick={async () => {
                  if (window.confirm(
                    "Remove teacher attendance from all future sessions of this class?\n\n" +
                    "Teacher attendance for sessions that have already run is kept.\n\n" +
                    "Press OK to remove, or Cancel to go back."
                  )) {
                    await removeFutureTeacherAttendance({ variables: { courseId: Number(id) } });
                    refetch({ variables: { courseId: Number(id) } });
                  }
                }}
              />

              <Button
                color='teal'
                basic
                icon='user plus'
                content='Add teacher attendance to future classes'
                onClick={async () => {
                  if (window.confirm(
                    "Add teacher attendance to all future sessions of this class?\n\n" +
                    "This adds the class's teachers to every future session, skipping any session that already has an entry for them.\n\n" +
                    "Press OK to add, or Cancel to go back."
                  )) {
                    await addFutureTeacherAttendance({ variables: { courseId: Number(id) } });
                    refetch({ variables: { courseId: Number(id) } });
                  }
                }}
              />

              <Divider />

              {/* Moving a student keeps their attendance with them: the history is re-created on
                  the other class at the times this one ran, and this class loses them entirely. */}
              <Header as='h4'>Move a student to another class</Header>
              {courseBlockTagId == null ? (
                <p className={styles.muted}>This class has no block tag, so there is nowhere to move anyone to.</p>
              ) : (
                <>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Student</label>
                    <Dropdown
                      fluid
                      selection
                      search
                      clearable
                      placeholder='Who is moving'
                      noResultsMessage='Nobody is on this class'
                      value={moveStudentId ?? ''}
                      options={(data?.course?.participants ?? []).map((p: any) => ({
                        key: p.id,
                        value: Number(p.id),
                        text: p.identifier ? `${p.name} (${p.identifier})` : p.name,
                      }))}
                      onChange={(_: any, { value }: any) =>
                        setMoveStudentId(value === '' || value == null ? null : Number(value))}
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Move to</label>
                    <Dropdown
                      fluid
                      selection
                      search
                      clearable
                      placeholder='Which class in this block'
                      noResultsMessage='No other class in this block'
                      value={moveTargetCourseId ?? ''}
                      options={(blockClassData?.classes ?? [])
                        .filter((c: any) => Number(c.id) !== Number(id))
                        .map((c: any) => ({
                          key: c.id,
                          value: Number(c.id),
                          text: `${c.name} (${c.SKU})`,
                        }))}
                      onChange={(_: any, { value }: any) =>
                        setMoveTargetCourseId(value === '' || value == null ? null : Number(value))}
                    />
                  </div>
                  <Button
                    color='blue'
                    basic
                    icon='exchange'
                    content='Move student'
                    loading={moveBusy}
                    disabled={moveBusy || moveStudentId == null || moveTargetCourseId == null}
                    onClick={moveStudent}
                  />
                  <p className={styles.muted}>
                    Their attendance so far is copied into the other class at the times this class
                    ran, with the same marks and no teacher attendance, and they are removed from
                    this class completely. Only classes in this block can be chosen.
                  </p>
                  {moveError && (
                    <Message negative size='tiny'>
                      <Icon name='warning sign' />
                      {moveError}
                    </Message>
                  )}
                </>
              )}

              <Divider />

              <Button
                color='red'
                basic
                icon='trash'
                content='Delete class'
                onClick={async () => {
                  if (window.confirm(
                    "⚠️ Permanently delete this class?\n\n" +
                    "This will remove ALL records from the database — sessions, attendance history, everything. This cannot be undone.\n\n" +
                    "Only use this if the class was created by mistake. If the class ran but is now finished, use Archive instead.\n\n" +
                    "Press OK to permanently delete, or Cancel to go back."
                  )) {
                    await deleteCourse({ variables: { courseId: Number(id) }});
                    navigate(-1)
                  }
                }}
              />
            </Segment>
          )}
          <Divider horizontal>
            <Header as='h4'>
              Info
            </Header>
          </Divider>
          <AttendanceGraph data={data} />

          <div className={styles.infoGrid}>
            <span className={styles.infoLabel}>SKU</span>
            <span className={styles.infoValue}>{data && data.course && data.course.SKU}</span>

            <span className={styles.infoLabel}>Teachers</span>
            <span className={styles.infoValue}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5em' }}>
                {data?.course?.teachers?.map((o: any) => (
                  <Label key={o.id} image basic style={{ margin: 0 }}>
                    <Link to={`${PATHS.PEOPLE}/${o.id}`}>{o.name}</Link>
                    {data?.course?.canEdit && (
                      <Icon
                        name="delete"
                        title={`Remove ${o.name} from this class`}
                        onClick={() => {
                          if (teacherBusy) return;
                          // Worth confirming: this also clears their attendance rows for
                          // every future session of the class.
                          if (window.confirm(
                            `Remove ${o.name} as a teacher of this class?\n\n` +
                            'They lose access to the class and its students, and are taken off ' +
                            'the register for future sessions.\n\n' +
                            'Past sessions keep their record, and the class will still show that ' +
                            'they taught it.'
                          )) {
                            changeTeacher(o.id, false);
                          }
                        }}
                      />
                    )}
                  </Label>
                ))}

                {(data?.course?.teachers ?? []).length === 0 && (
                  <span style={{ color: '#999', fontStyle: 'italic' }}>No teachers</span>
                )}

                {data?.course?.canEdit && (
                  <Dropdown
                    selectOnBlur={false}
                    value=""
                    loading={teacherBusy}
                    disabled={teacherBusy}
                    text="Add teacher"
                    options={(teacherCandidateData?.teacherCandidates ?? [])
                      .filter((c: any) => !(data?.course?.teachers ?? []).some((t: any) => t.id === c.id))
                      .map((c: any) => ({ key: c.id, value: c.id, text: c.name }))}
                    onChange={(_: any, { value }: any) => changeTeacher(Number(value), true)}
                    selection
                    search
                    style={{ minWidth: '12em' }}
                  />
                )}
              </div>

              {teacherError && <Message error size="tiny" content={teacherError} style={{ marginTop: '0.5em' }} />}
            </span>

            <span className={styles.infoLabel}>Manager</span>
            <span className={styles.infoValue}>
              {data?.course?.manager ? (
                <Link to={`${PATHS.PEOPLE}/${data.course.manager.id}`}>{data.course.manager.name}</Link>
              ) : (
                <span style={{ color: '#999', fontStyle: 'italic' }}>Unassigned</span>
              )}
            </span>

            <span className={styles.infoLabel}>Attendance</span>
            <span className={styles.infoValue}>{data && data.course && Math.round(data.course.attendancePercentage * 100)}%</span>

            <span className={styles.infoLabel}>Participants</span>
            <span className={styles.infoValue}>{data && data.course && data.course.courseParticipantCount}</span>

            <span className={styles.infoLabel}>Tags</span>
            <span className={styles.infoValue}>
              <CourseTags tags={data && data.course && data.course.tags} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />
            </span>
          </div>
          {editMode && <CourseAdmin participants={(data && data.course && data.course.futureParticipants) || []} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />}
          <Divider horizontal>
            <Header as='h4'>
              Sessions
            </Header>
          </Divider>
          {loading && (<Dimmer active inverted>
              <Loader inverted content='Loading' />
          </Dimmer>)
          }
          <Accordion style={{ marginBottom: '3em' }}>
            {!loading && !error && data != null && data.course && data.course.sessions.map((s: any) => {

              if (!editMode && s.reasonForCancellation != null && s.reasonForCancellation === 'Other') {
                return null; // Skip sessions that are cancelled with 'Other' reason
              }

              const inFuture = s.startTime > Number(new Date())

              const adjustTime = (magnitude: number, forwardDirection: boolean, unit: string) => async (e: any) => {
                e.stopPropagation()
                await updateSessionTime({ variables: { sessionId: s.id, magnitude, forwardDirection, unit } });
                refetch({ variables: { courseId: Number(id) } })
              }

              return (
              <Fragment key={s.id}>
                <Accordion.Title
                  style={s.startTime > TenHoursAgo ? {}: { opacity: "0.51"}}
                  active={activeId === s.id}
                  index={0}
                  onClick={() => {
                    if (s.id !== activeId) {
                      setIdentifier('');
                      setActiveId(s.id)
                    } else {
                      setActiveId(undefined)
                    }
                    setHasUsedAccordian(true)
                  }}
                >
                {s.reasonForCancellation != null ? (
                  <b>Class cancelled on {formatDateTimeToDay(s.startTime)}</b>
                ) : (
                  <div className={styles.sessionTitle}>
                    <span className={styles.sessionMeta}>
                      <Icon name='dropdown' />
                      <Icon name='user' /> {s.attendanceCount}
                    </span>
                    <b>{formatDateTime(s.startTime)}</b>
                    {!inFuture ? `(attendance: ${Math.round(s.attendancePercentage*100)}%)` : ""}
                    {isAdmin && editMode && (
                      <span className={styles.sessionControls}>
                        <Button size='mini' basic color='red' onClick={async (e:any) => {
                          e.stopPropagation()
                          await deleteSession({ variables: { sessionId: s.id, reason: 'Teacher sick'} })
                          refetch({ variables: { courseId: Number(id) } })
                        }}>Cancel session</Button>
                        <Button.Group size='mini' basic>
                          <Button onClick={adjustTime(1, false, "days")}>-1d</Button>
                          <Button onClick={adjustTime(1, false, "hours")}>-1h</Button>
                          <Button onClick={adjustTime(15, true, "minutes")}>+15m</Button>
                          <Button onClick={adjustTime(1, true, "hours")}>+1h</Button>
                          <Button onClick={adjustTime(1, true, "days")}>+1d</Button>
                        </Button.Group>
                      </span>
                    )}
                  </div>
                )}
                </Accordion.Title>
                {s.reasonForCancellation != null ? (
                  isAdmin && editMode ? (
                    <Dropdown
                        placeholder='Reason...'
                        selection
                        value={s.reasonForCancellation}
                        onChange={async (_, d) =>  {
                          await deleteSession({ variables: { sessionId: s.id, reason: d.value} })
                          setTimeout(() => refetch({ courseId: Number(id) }), 2000)
                        }}
                        options={CANCELLATION_REASON}
                    />
                  ) : (
                    <div className={styles.muted}>Reason: {s.reasonForCancellation}</div>
                  )
                ) : (
                  <Accordion.Content active={activeId === s.id}>
                    <Session canEditCourse={data && data.course && data.course.canEdit} session={s as any} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />
                    {isAdmin && editMode && (
                      <div className={styles.addPersonRow}>
                        <Input
                            placeholder='StudentId to add'
                            value={identifier.toLocaleUpperCase()}
                            onKeyPress={async (e: any) => {
                               if (e.key === 'Enter') {
                                  await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PENDING' } })
                                  refetch({ variables: { courseId: Number(id) } })
                                  setIdentifier('')
                               }
                            }}
                            onChange={(e: any) => setIdentifier(e.target.value)}
                        />
                        <Button onClick={async () => {
                          await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PENDING' } })
                          refetch({ variables: { courseId: Number(id) } })
                          setIdentifier('')
                        }}>Add</Button>
                        <Button onClick={async () => {
                          await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PRESENT' } })
                          refetch({ variables: { courseId: Number(id) } })
                          setIdentifier('')
                        }}>Add as Present</Button>
                      </div>
                    )}
                  </Accordion.Content>
                )}
              </Fragment>
            )
            })
          }
          </Accordion>
            {isAdmin && editMode && (
              <div className={styles.addSessionRow}>
                <DatePicker
                    selected={newSessionDate}
                    dateFormat="dd/MM/yyyy"
                    onChange={(date) => {
                        if (date == null) return;
                        setNewSessionDate(date);
                    }}
                    showYearDropdown
                    maxDate={new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate())}
                />
                <Button
                  onClick={async () => {
                    await addSessionToCourse({ variables: { courseId: Number(id), date: newSessionDate?.toISOString() } })
                    // move new session date forward 7 days
                    if (newSessionDate == null) return;
                    const newDate = new Date(newSessionDate.getTime() + 7 * 24 * 60 * 60 * 1000);
                    setNewSessionDate(newDate);
                    refetch({ variables: { courseId: Number(id) } })
                  }}
                >
                  Add session
                </Button>
              </div>
            )}
        </Container>
      </Segment>

    </ResponsiveContainer>
  );
}

export { AdminClassView }
